import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Separator } from '@fs/zion-ui'
import {
  getNextUuid,
  useSlsStateContext,
  getPersonRelationships,
  getNestedOrFloatingElementById,
  getPersonName,
} from '@fs/zion-sls'

const SimpleRelationshipsList = ({ person, topElement, emptyContent, singularTopElement, pluralTopElement }) => {
  const { images } = useSlsStateContext()

  const currentPersonRelationships = useMemo(() => {
    if (!person || !images) return []
    return getPersonRelationships(person, images, false) || []
  }, [person, images])

  const currentPersonRelationshipsList = useMemo(() => {
    if (!images) return []
    return currentPersonRelationships.map((rel) => {
      const otherPersonId = rel.otherPersonId
      const otherPerson = otherPersonId ? getNestedOrFloatingElementById(images, otherPersonId) : null
      const otherPersonName = otherPerson ? getPersonName({ person: otherPerson, justOneName: true }) : ''

      return {
        id: rel.id || otherPersonId || `rel-${getNextUuid()}`,
        relationshipType: rel.label,
        personName: otherPersonName,
      }
    })
  }, [currentPersonRelationships, images])

  if (currentPersonRelationshipsList.length === 0) {
    return emptyContent || null
  }

  // Determine which top element to render based on the presence of topElement prop and the number of relationships
  const componentToRender =
    (currentPersonRelationshipsList.length === 1 ? singularTopElement : pluralTopElement) || topElement

  return (
    <>
      {componentToRender}
      <Separator size="xs" />
      <ul>
        {currentPersonRelationshipsList.map((rel) => (
          <li key={rel.id} data-testid={`relationship-item-${rel.id}`}>
            {`${rel.relationshipType}: ${rel.personName}`}
          </li>
        ))}
      </ul>
    </>
  )
}

SimpleRelationshipsList.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.string,
  }),
  topElement: PropTypes.node,
  singularTopElement: PropTypes.node,
  pluralTopElement: PropTypes.node,
  emptyContent: PropTypes.node,
}

SimpleRelationshipsList.defaultProps = {
  person: undefined,
  topElement: null,
  singularTopElement: null,
  pluralTopElement: null,
  emptyContent: null,
}

export default SimpleRelationshipsList
